import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import useExecutionStatus from '@features/scripts/hooks/useExecutionStatus';
import useExecutionControl from '@features/scripts/hooks/useExecutionControl';
import { useWorkOrderRunsQuery } from '@api/bff/runs/useRuns';
import type { WorkOrderRunDetail } from '@api/bff/runs/useRuns';
import type { TestRun } from '@features/scripts/types';

const LIVE_STATUSES = ['PENDING', 'RUNNING', 'PAUSED'];

const toSavedTestRun = (run: WorkOrderRunDetail): TestRun => ({
  id: run.id,
  scriptId: '',
  globalId: run.jobNumber ?? '',
  status: run.status as TestRun['status'],
  overallResult: run.overallResult,
  measurementResults: run.measurementResults,
  eventLog: run.eventLog,
  startedBy: run.startedBy,
  startedAt: run.startedAt,
  completedAt: run.completedAt,
  createdAt: run.createdAt,
  dcRuntimeId: run.dcRuntimeId ?? null
});

export const useActiveWorkOrderRun = (jobNumber: string) => {
  const queryClient = useQueryClient();
  const runsQuery = useWorkOrderRunsQuery(jobNumber, 1, 1);
  const latestRun = runsQuery.data?.data?.[0] ?? null;

  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [activeRun, setActiveRun] = useState<WorkOrderRunDetail | null>(null);

  const execution = useExecutionStatus(activeRunId);
  const control = useExecutionControl(activeRunId);
  const { loadFromSaved } = execution;

  const restoredRunIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (activeRunId || !latestRun) return;
    if (!LIVE_STATUSES.includes(latestRun.status)) return;
    if (restoredRunIdRef.current === latestRun.id) return;

    restoredRunIdRef.current = latestRun.id;
    setActiveRunId(latestRun.id);
    setActiveRun(latestRun);
    setTimeout(() => loadFromSaved(toSavedTestRun(latestRun)), 50);
  }, [latestRun, activeRunId, loadFromSaved]);

  const liveStatus = execution.runStatus?.status ?? activeRun?.status ?? null;
  const isRunActive = !!activeRunId && LIVE_STATUSES.includes(liveStatus ?? '');

  const finishedStatusRef = useRef<string | null>(null);
  useEffect(() => {
    if (!liveStatus || LIVE_STATUSES.includes(liveStatus)) return;
    if (finishedStatusRef.current === `${activeRunId}:${liveStatus}`) return;
    finishedStatusRef.current = `${activeRunId}:${liveStatus}`;
    queryClient.invalidateQueries({ queryKey: ['workOrderRuns'] });
    queryClient.invalidateQueries({ queryKey: ['testRuns'] });
  }, [liveStatus, activeRunId, queryClient]);

  const isDryRun = useMemo(() => {
    if (!activeRun) return false;
    const cells = activeRun.cells ?? [];
    return (
      cells.length > 0 &&
      !cells.some(c => c.equipmentRef?.globalId || c.equipmentRef?.id)
    );
  }, [activeRun]);

  return {
    activeRunId,
    activeRun,
    latestRun,
    status: liveStatus,
    isRunActive,
    isDryRun,
    runStatus: execution.runStatus,
    itemStatus: execution.itemStatus,
    events: execution.events,
    measurements: execution.measurements,
    control
  };
};
